import { Injectable, inject } from '@angular/core';
import {setLocale} from '@app/shared/_helpers/date';
import {Meta} from '@app/shared/_interfaces/meta';
import * as dot from 'dot-object';
import {PrimeNG} from 'primeng/config';

@Injectable({
  providedIn: 'root'
})
export class TranslateService {
  private config = inject(PrimeNG);


  private translations: Array<object> = [];
  private supportedLanguages: Array<string> = [];
  private currentLanguage: string;
  private fallbackLanguage: string;

  getSupportedLanguages(): Array<string> {
    return this.supportedLanguages;
  }

  isSupportedLanguage(lang: string): boolean {
    return this.supportedLanguages.indexOf(lang) > -1;
  }

  getFallbackLanguage(): string {
    return this.fallbackLanguage;
  }

  getCurrentLanguage(): string {
    return this.currentLanguage;
  }

  setFallbackLanguage(lang: string): void {
    if (!this.isSupportedLanguage(lang)) {
      throw new Error(
        `${lang} isn't supported language.`
      );
    }
    this.fallbackLanguage = lang;
  }

  setTranslation(lang: string, t: any) {
    if (!this.isSupportedLanguage(lang)) {
      throw new Error(
        `${lang} isn't supported language.`
      );
    }
    this.translations[lang] = t;
  }

  setSupportedLanguages(langs: string[]) {
    this.supportedLanguages = langs;
  }

  setActiveLanguage(lang: string) {

    if (!this.fallbackLanguage) {
      throw new Error('Fallback language should be configured');
    }

    if (!this.isSupportedLanguage(lang)) {
      lang = this.fallbackLanguage;
    }

    this.currentLanguage = lang;
    setLocale(this.currentLanguage);
    if (this.translations[lang]?.primeng){
      this.config.setTranslation(this.translations[lang]?.primeng);
    }
  }

  private has(key: string) {
    if (this.translations.hasOwnProperty(this.currentLanguage) && this.translations[this.currentLanguage].hasOwnProperty(key)) {
      return true;
    } else if (this.translations.hasOwnProperty(this.fallbackLanguage) && this.translations[this.fallbackLanguage].hasOwnProperty(key)) {
      return true;
    }
    return false;
  }

  private get(key: string) {

    if (key === 'primeng'){
      return 'primeng';
    }

    if (this.translations.hasOwnProperty(this.currentLanguage) && this.translations[this.currentLanguage].hasOwnProperty(key)) {
      return this.translations[this.currentLanguage][key];
    } else if (this.translations.hasOwnProperty(this.fallbackLanguage) && this.translations[this.fallbackLanguage].hasOwnProperty(key)) {
      return this.translations[this.fallbackLanguage][key];
    }
    return key;
  }

  switchTranslate(wishKey: string, defaultKey: string, mapKeys?: object) {
    return this.translate(this.has(wishKey) ? wishKey : defaultKey, mapKeys);
  }

  translate(key: string, mapKeys?: object): string {

    const errors = [];

    if (!this.currentLanguage) {
      errors.push(`currentLanguage is null currentLanguage=${this.currentLanguage} key=${key}`);
    }
    if (!this.fallbackLanguage) {
      errors.push(`fallbackLanguage is null fallbackLanguage=${this.fallbackLanguage} key=${key}`);
    }

    if (errors.length > 0) {
      return key;
    }

    let res = this.get(key);

    if (mapKeys) {
      Object.keys(mapKeys).forEach(mapKey => {
        res = res.replaceAll('{' + mapKey + '}', mapKeys[mapKey]);
      });
    }
    return res;
  }

  translateObject(data: object, involvedKeys: string[], mapKeys?: object): object {
    let newData = {...data};


    for (let invKey of involvedKeys){
      const curVal = dot.pick(invKey, newData);
      if (curVal) {
        dot.set(invKey, this.translate(curVal, mapKeys), newData);
      }
    }

    return newData;
  }

  getTranslations(): Array<object> {
    return this.translations;
  }

  getCurrentTranslations(): Array<object> {
    return this.translations[this.currentLanguage];
  }

  reloadByConfig(res: Meta.GlobalAppConfig) {
    const {translations, config} = res;
    this.setSupportedLanguages([...config.supported_languages, config.current_language]);

    this.setTranslation(
      config.current_language,
      translations[config.current_language]
    );


    this.setTranslation(
      config.fallback_language,
      translations[config.fallback_language]
    );

    this.setFallbackLanguage(config.fallback_language);
    this.setActiveLanguage(config.current_language);
  }

}
